<template>
  <b-overlay :show="loading">
    <div class="pt-2 pb-2 pl-2 border card shadow max-width-800" style="border-radius:10px; font-weight: 600;">
      <div class="pb-1">
        Report - Medication
      </div>

      <b-row>
        <b-col md="3">
          <b-form-datepicker
            :value="dateStart"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="From"
            @input="(val) => dateStart = val"
          />
        </b-col>
        <b-col md="3">
          <b-form-datepicker
            :value="dateEnd"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="To"
            :min="dateStart"
            @input="(val) => dateEnd = val"
          />
        </b-col>

        <b-col md="3">
          <v-select
            class="filter-select"
            placeholder="Status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :value="statusFilter"
            :options="statusOptions"
            :reduce="val => val.value"
            :disabled="loading"
            @input="(val) => statusFilter = val"
          />
        </b-col>

        <b-col md="3" class="pr-2">
          <drug-filter-select 
            :value="drug_id"
            @change="val => drug_id = val"
          />
        </b-col>
      </b-row>
    </div>

    <div>
      <b-row class="match-height">
        <b-col class="max-width-800">
          <report-pie-chart :title="'Medication Delivery Status'" :series="prescriptionStatusChartSeries" :chart-options="prescriptionChartOptions"></report-pie-chart>
        </b-col>
      </b-row>
    </div>

    <div class="max-width-800">
      <list-table
        :records="records" 
        :columns="tableColumns"
        :current-page-number="1"
        :show-import-button="false"
        :show-export-button="can('medication_reports:CanExport')"
        :show-search="false"
        :total-records="totalRecords"
        @export="onExport"
      >
      </list-table>
    </div>

  </b-overlay>
</template>

<script>
import { debounce, get } from "lodash";

import { 
  BAlert, 
  BAvatar,
  BBadge, 
  BImg, 
  BCard, 
  BCol, 
  BLink, 
  BOverlay, 
  BRow, 
  BFormDatepicker, 
  BCardImg 
} from "bootstrap-vue"

import { startOfMonth } from 'date-fns'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";
import ListTable from "@/@core/components/ListTable/ListTable.vue";

import DrugFilterSelect from "@core/components/shared/admin/selects/DrugFilterSelect.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ReportPieChart from "@core/components/shared/admin/reports/view/ReportPieChart.vue"

const watchHandler = {
  handler(){
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const chartColors = [
  'rgba(7, 125, 188, 1)',
  'rgba(101, 163, 13, 1)',
  'rgba(7, 125, 188, 0.7)',
  'rgba(101, 163, 13, 0.7)',
  'rgba(75, 75, 75, 0.2)',
  'rgba(7, 125, 188, 0.6)',
  'rgba(101, 163, 13, 0.6)',
  'rgba(7, 125, 188, 0.4)',
  'rgba(101, 163, 13, 0.4)',
  'rgba(7, 125, 188, 0.2)'
]

export default {
  components: {
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    vSelect,
    BFormDatepicker,
    ReportPieChart,
    DrugFilterSelect,
    ListTable
  },
  directives: {
    Ripple,
  },
  data(){
    const monthBeginning = startOfMonth(new Date())
    return {
      loading: false,
      
      dateStart: this.formatDate(monthBeginning, 'yyyy-MM-dd'),
      dateEnd: this.formatDate(new Date(), 'yyyy-MM-dd'),
      statusFilter: "",
      drug_id: null,
      records: [],

      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Processing', value: 'processing' },
        { label: 'Delivered', value: 'delivered' },
      ],
      
      prescriptionStatusChartSeries: [0, 0, 0],
      prescriptionChartOptions: {
        chart: {
          type: 'pie',
        },
        colors: ['rgba(7, 125, 188, 1)', 'rgba(101, 163, 13, 1)', 'rgba(7, 125, 188, 0.7)'],
        labels: ['Pending - 0', 'Processing - 0', 'Delivered - 0']
        
      },
      
      tableColumns: [
        { key: 'drug.generic_name', label: 'Generic Name', sortable: false, },
        { key: 'drug.brand_name', label: 'Brand Name', sortable: false, formatter: val => val || '-', },
        { key: 'drug.route.name', label: 'Route', sortable: false },
        { key: 'drug.formulation.name', label: 'Formulation', sortable: false, },
        { key: 'drug.concentration.name', label: 'Concentration', sortable: false, },
        { key: 'pending', label: "Pending", sortable: false, },
        { key: 'delivered', label: "Delivered", sortable: false, },
      ],
      
      debounceFetchListData: null,
      debounceFetchAllCharts: null,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debounceFetchListData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    dateStart: watchHandlerAndResetPageNumber,
    dateEnd: watchHandlerAndResetPageNumber,
    statusFilter: watchHandlerAndResetPageNumber,
    drug_id: watchHandlerAndResetPageNumber,
  },
  created() {
    this.debounceFetchListData = debounce(this.fetchListData, 500);
    this.debounceFetchAllCharts = debounce(this.fetchAllCharts, 500);

    this.fetchListData()
    this.fetchAllCharts()

    const {
      from,
      to,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.dateStart = from;
    this.dateEnd = to;
    this.currentPage = +page;
    this.perPage = +limit;
  },
  methods: {
    async fetchListData() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          limit: this.perPage,
          page: this.currentPage,
          status: this.statusFilter,
          drug_id: this.drug_id,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.medicationReports.list(query);

        const { data } = request.data;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAllCharts() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          status: this.statusFilter,
          drug_id: this.drug_id,
        }

        const response = await this.useJwt().mainService.medicationReports.getChart(query);
        const [ response_data ] = get(response, 'data.data')

        if (response_data){
          this.prescriptionStatusChartSeries = Object.values(response_data)
          this.prescriptionChartOptions = {
            chart: {
              type: 'pie',
            },
            colors: chartColors,
            labels: Object.entries(response_data).map(([key, value]) => `${this.sentenceCase(key)} - ${value}`)
          };
        } else {
          this.prescriptionStatusChartSeries = [0, 0, 0];
          this.prescriptionChartOptions = {
            chart: {
              type: 'pie',
            },
            colors: chartColors,
            labels: ['Pending - 0', 'Processing - 0', 'Delivered - 0']
          };
        } 
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          status: this.statusFilter,
          drug_id: this.drug_id,
        }

        const response = await this.useJwt().mainService.medicationReports.export(query);
        FileDownload(response.data, "medication-report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    }, 
  }
}
</script>

<style scoped>

</style>

